/**
 * One kind of drop that is a bet, on one line of the grid: the label (its
 * words behind ⓘ) · two small choices — same as the rest · a different mix (the hit-again line: the texts of the drop that hit · new texts) — ·
 * the texts on each such drop (`MixCell`: asked → what really goes out) ·
 * how many of today's drops are of that kind (`TodayCell`), with those
 * drops as mini tiles on one line under the row. The row appears with its
 * siblings one after the other.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { FlaskConical, ListPlus, type LucideIcon, Repeat, RotateCcw, Search, TrendingDown, TrendingUp, Zap } from 'lucide-react';
import { DROP_GEN_COPY_KINDS, DROP_GEN_DROP_KIND_HINTS, DROP_GEN_DROP_KIND_WORDS, type DropGenPolicyKind, type DropGenTextMix, type DropGenTextMixInput, effectiveMix, keepsCopiedTexts, mixFor, RECOMMENDED_BET_MIX } from '@trafficpulse/shared';

import { HintDot } from '@/components/ui';
import { cn } from '@/lib/utils';

import { CHOICE_CHIP, CHOICE_OFF, CHOICE_ON } from '../../../../general/controls/field-style';
import type { TodayDrop } from './kind-facts';
import { MixCell } from './MixCell';
import { TodayCell, TodayTiles } from './TodayCell';

/** The section's columns — label · choices · texts · today; every row is a subgrid of it, so the columns line up. */
export const POLICY_COLS = 'grid grid-cols-1 gap-x-3 sm:grid-cols-[minmax(12rem,1fr)_auto_auto_minmax(5rem,1fr)]';
export const POLICY_ROW = 'grid grid-cols-1 items-center gap-x-3 gap-y-1.5 sm:col-span-4 sm:grid-cols-subgrid';

const ICONS: Record<DropGenPolicyKind, LucideIcon> = {
  HIT_AGAIN: Repeat,
  SCALED: TrendingUp,
  SHRUNK: TrendingDown,
  NEW_LIST: ListPlus,
  RETURNING: RotateCcw,
  EXPERIMENT: FlaskConical,
  EXTRA: Zap,
  PROBE: Search,
};

interface Props {
  kind: DropGenPolicyKind;
  /** The ordinary mix and the kinds' own — the engine's input. */
  cfg: DropGenTextMixInput;
  index: number;
  onSet: (v: DropGenTextMix | undefined) => void;
  /** Today's drops of this kind; null = no day laid out yet. */
  drops: TodayDrop[] | null;
  /** The reuse pool's size; null while the ranking is being read. */
  poolSize: number | null;
}

const Choice: FC<{ on: boolean; label: string; onClick: () => void }> = ({ on, label, onClick }) => (
  <button type="button" aria-pressed={on} onClick={onClick} className={cn(CHOICE_CHIP, 'h-7 px-2.5 text-xs', on ? CHOICE_ON : CHOICE_OFF)}>
    {label}
  </button>
);

export const PolicyRow: FC<Props> = ({ kind, cfg, index, onSet, drops, poolSize }) => {
  const Icon = ICONS[kind];
  const value = cfg.policy?.[kind];
  const own = value !== undefined;
  // Phase 38 — a hit-again drop keeps the texts of the drop that hit unless it is given new texts (a mix of its own).
  const copies = keepsCopiedTexts(cfg, kind);
  const asked = mixFor(cfg, kind);
  const sends = poolSize === null ? null : effectiveMix(cfg, kind, poolSize);
  return (
    <div className={cn(POLICY_ROW, 'border-t border-border/40 py-2 fill-mode-backwards animate-in fade-in-0 slide-in-from-top-1 duration-300 motion-reduce:animate-none')} style={{ animationDelay: `${index * 60}ms` }} data-testid={`policy-${kind}`}>
      <span className="flex min-w-0 items-center gap-2 text-xs">
        <Icon className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
        <span className="truncate">{DROP_GEN_DROP_KIND_WORDS[kind]}</span>
        <HintDot label={DROP_GEN_DROP_KIND_HINTS[kind]} />
      </span>
      <span className="inline-flex items-center gap-1">
        {/* The way back keeps its name while the other choice is picked (15/10/2026: it read "same as the rest"); 21/10/2026: "own" was not clear — new texts / a different mix. */}
        <Choice on={!own} label={DROP_GEN_COPY_KINDS.includes(kind) ? 'the texts of the drop that hit' : 'same as the rest'} onClick={() => onSet(undefined)} />
        <Choice on={own} label={DROP_GEN_COPY_KINDS.includes(kind) ? 'new texts: ★ proven + ✦ AI' : 'a different mix'} onClick={() => onSet(own ? value : { ...RECOMMENDED_BET_MIX })} />
      </span>
      <MixCell kindWords={DROP_GEN_DROP_KIND_WORDS[kind]} asked={asked} sends={sends} onSet={own ? (m) => onSet(m) : undefined} copy={copies} />
      <TodayCell kind={kind} drops={drops} />
      {drops && drops.length > 0 ? (
        <div className="pl-[22px] sm:col-span-4">
          <TodayTiles kind={kind} drops={drops} />
        </div>
      ) : null}
    </div>
  );
};
